import React, { useState } from 'react';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { portalApi, formatApiError } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { User, Key } from '@phosphor-icons/react';

export default function PortalProfile() {
  const { customer } = usePortalAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [nextRepeat, setNextRepeat] = useState('');
  const [busy, setBusy] = useState(false);

  const c = customer || {};
  const isBiz = c.customer_type === 'business' || c.company_name;
  const displayName = isBiz ? c.company_name : `${c.first_name || ''} ${c.last_name || ''}`.trim();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error('Neues Passwort muss mindestens 8 Zeichen haben.');
    if (next !== nextRepeat) return toast.error('Passwörter stimmen nicht überein.');
    setBusy(true);
    try {
      await portalApi.changePassword({ current_password: current, new_password: next });
      toast.success('Passwort geändert.');
      setCurrent(''); setNext(''); setNextRepeat('');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="portal-profile-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Profil</h1>
        <p className="text-[#525866] mt-1">Ihre Konto-Daten & Passwort</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-swiss p-6" data-testid="profile-data-card">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-700 mb-4 flex items-center gap-2">
            <User weight="bold" size={16} /> Ihre Daten
          </h2>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-[#E2E4E9] pb-2"><dt className="text-[#525866]">Name / Firma</dt><dd className="font-medium text-[#111418] text-right">{displayName}</dd></div>
            {isBiz && (
              <div className="flex justify-between border-b border-[#E2E4E9] pb-2"><dt className="text-[#525866]">Ansprechpartner</dt><dd className="font-medium text-[#111418] text-right">{c.first_name} {c.last_name}</dd></div>
            )}
            <div className="flex justify-between border-b border-[#E2E4E9] pb-2"><dt className="text-[#525866]">E-Mail</dt><dd className="font-medium text-[#111418] text-right">{c.email}</dd></div>
            <div className="flex justify-between border-b border-[#E2E4E9] pb-2"><dt className="text-[#525866]">Telefon</dt><dd className="font-medium text-[#111418] text-right">{c.phone}</dd></div>
            <div className="flex justify-between border-b border-[#E2E4E9] pb-2"><dt className="text-[#525866]">Adresse</dt><dd className="font-medium text-[#111418] text-right">{c.address}<br />{c.postal_code} {c.city}</dd></div>
            {c.vat_id && <div className="flex justify-between"><dt className="text-[#525866]">USt-IdNr.</dt><dd className="font-medium text-[#111418]">{c.vat_id}</dd></div>}
          </dl>
          <p className="text-xs text-[#8A94A6] mt-4">Bei Datenänderungen kontaktieren Sie bitte Ihren Vermieter.</p>
        </div>

        <div className="card-swiss p-6" data-testid="profile-password-card">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-700 mb-4 flex items-center gap-2">
            <Key weight="bold" size={16} /> Passwort ändern
          </h2>
          <form onSubmit={handleChangePassword} className="space-y-4" data-testid="portal-change-pw-form">
            <div>
              <Label className="overline">Aktuelles Passwort</Label>
              <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required className="input-swiss" data-testid="pw-current" />
            </div>
            <div>
              <Label className="overline">Neues Passwort (min. 8 Zeichen)</Label>
              <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} className="input-swiss" data-testid="pw-new" />
            </div>
            <div>
              <Label className="overline">Neues Passwort wiederholen</Label>
              <Input type="password" value={nextRepeat} onChange={(e) => setNextRepeat(e.target.value)} required className="input-swiss" data-testid="pw-new2" />
            </div>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold w-full py-2.5 rounded" disabled={busy} data-testid="pw-submit">
              {busy ? 'Wird gespeichert…' : 'Passwort ändern'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
